import { useFragment } from "@/generated";
import { ProjectFieldsFragmentDoc, UnlinkProjectDocument } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import type { Reference } from "@apollo/client";
import { useMutation } from "@apollo/client/react";

/**
 * Hook for unlinking a project and updating the Apollo cache.
 *
 * Converts a linked project copy to an independent project by:
 * 1. Setting isLinkedCopy to false
 * 2. Removing the project from the original project's linkedCopies
 *
 * **Cache Strategy:**
 * Uses cache.modify() to update both the unlinked project and the original project's
 * linkedCopies in the normalized cache.
 */
export function useUnlinkProject() {
  const [unlinkProjectMutation, { loading }] = useMutation(UnlinkProjectDocument);

  const unlinkProject = async (projectId: string, originalProjectId: string | null) => {
    return withToast(
      async () => {
        const result = await unlinkProjectMutation({
          variables: {
            projectId,
          },
          update(cache, { data }) {
            if (!data?.unlinkProject) return;

            // Update the unlinked project
            cache.modify({
              id: `Project:${projectId}`,
              fields: {
                isLinkedCopy: () => false,
              },
            });

            // Update the original project's linkedCopies
            if (originalProjectId) {
              const originalId = cache.identify({
                __typename: "Project",
                id: originalProjectId,
              });
              if (originalId) {
                cache.modify({
                  id: `Project:${originalProjectId}`,
                  fields: {
                    linkedCopies: (existingData, { readField }) => {
                      return existingData.filter(
                        (ref: Reference) => readField("id", ref) !== projectId
                      );
                    },
                  },
                });
              }
            }
          },
        });

        const maskedProject = result.data?.unlinkProject;
        const unlinkedProject = maskedProject
          ? useFragment(ProjectFieldsFragmentDoc, maskedProject)
          : undefined;

        return unlinkedProject;
      },
      {
        loading: "Unlinking project...",
        success: "Successfully unlinked project",
        error: "Failed to unlink project. Please try again.",
      },
      "unlinkProject"
    );
  };

  return {
    unlinkProject,
    loading,
  };
}
